import React from 'react';
import AuthImage from '@/components/AuthImage';
import { AiEstimateNotice } from '@/components/AiEstimateNotice';
import { motion } from 'framer-motion';
import { Calendar, StickyNote, Coffee, Utensils, Wine, X, Loader2, Microscope, Zap, Sparkles, Undo2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * FoodEntryDetailModal - Detailed view modal for a food entry
 * Shows full analysis, macros, micronutrients, and action buttons
 */
const FoodEntryDetailModal = ({
  entry,
  onClose,
  onRelabel,
  onRunAiAnalysis,
  onUndoFinalize,
  onAnalyzeMicronutrients,
  relabelling,
  runningAiAnalysis,
  undoingFinalization,
  analyzingMicros,
  showMicronutrientReminder,
  setShowMicronutrientReminder,
}) => {
  if (!entry) return null;

  // Get meal type color
  const getMealTypeColor = (type) => {
    const colors = {
      breakfast: 'bg-amber-500/90 text-white',
      lunch: 'bg-primary/90 text-white',
      dinner: 'bg-purple-500/90 text-white',
      snack: 'bg-pink-500/90 text-white',
      drink: 'bg-blue-500/90 text-white',
      late_night: 'bg-indigo-500/90 text-white',
      meal: 'bg-green-500/90 text-white'
    };
    return colors[type] || 'bg-gray-500/90 text-white';
  };

  // Get meal type icon
  const getMealTypeIcon = (type) => {
    const icons = {
      breakfast: <Coffee className="w-4 h-4" />,
      lunch: <Utensils className="w-4 h-4" />,
      dinner: <Utensils className="w-4 h-4" />,
      snack: <Coffee className="w-4 h-4" />,
      drink: <Wine className="w-4 h-4" />,
      late_night: <Utensils className="w-4 h-4" />,
      meal: <Utensils className="w-4 h-4" />
    };
    return icons[type] || <Utensils className="w-4 h-4" />;
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Check if micronutrients are available
  const hasMicronutrients = entry.vitamin_a_mcg || entry.vitamin_c_mg || entry.calcium_mg || 
    entry.iron_mg || entry.potassium_mg || entry.folate_mcg || entry.vitamin_b12_mcg;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="ph-no-capture fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-card rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="relative">
          <AuthImage
            src={entry.image_url}
            alt={entry.meal_type}
            className="w-full h-64 object-cover"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="absolute bottom-4 left-4">
            <span className={`inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold capitalize shadow-lg ${getMealTypeColor(entry.meal_type)}`}>
              {getMealTypeIcon(entry.meal_type)}
              {entry.meal_type}
            </span>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Date and Notes */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(entry.created_at)}</span>
            </div>
            {entry.notes && (
              <div className="flex items-start gap-2 text-muted-foreground">
                <StickyNote className="w-4 h-4 mt-1" />
                <span>{entry.notes}</span>
              </div>
            )}
          </div>

          {/* AI Analysis */}
          {entry.analysis && entry.analysis !== 'Analysis unavailable' && (
            <div className="p-4 bg-primary/5 rounded-xl">
              <p className="font-bold text-primary mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                AI Analysis
              </p>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                {entry.analysis}
              </p>
            </div>
          )}

          {/* No AI Analysis - Offer to run it */}
          {(!entry.analysis || entry.analysis === 'Analysis unavailable' || entry.analysis === '') && (
            <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <p className="font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-2" data-testid="analysis-status-modal">
                <Sparkles className="w-4 h-4" />
                {entry.analysis_error === 'ai_unavailable' ? 'Analysis temporarily unavailable' : 'No AI analysis available for this entry'}
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-500 mb-3">
                {entry.analysis_error === 'ai_unavailable'
                  ? 'Our analysis service could not be reached. Please try again shortly.'
                  : `Click below to run AI analysis and get nutritional insights for this ${entry.meal_type}.`}
              </p>
              <Button
                data-testid="run-ai-analysis-modal-button"
                onClick={() => onRunAiAnalysis(entry.id)}
                disabled={runningAiAnalysis === entry.id}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              >
                {runningAiAnalysis === entry.id ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {entry.analysis_error === 'ai_unavailable' ? 'Try again' : 'Run AI Analysis'}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Macronutrients */}
          {entry.is_finalized && (
            <div className="p-4 bg-secondary/5 rounded-xl">
              <p className="font-bold text-secondary mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Macronutrients
              </p>
              <AiEstimateNotice className="mb-2" />
              <div className="grid grid-cols-5 gap-3">
                <div className="text-center p-3 bg-amber-100 dark:bg-amber-950/30 rounded-xl">
                  <div className="font-bold text-2xl text-amber-600">{entry.calories || 0}</div>
                  <div className="text-xs text-muted-foreground">Calories</div>
                </div>
                <div className="text-center p-3 bg-primary/10 rounded-xl">
                  <div className="font-bold text-2xl text-primary">{entry.protein_g || 0}g</div>
                  <div className="text-xs text-muted-foreground">Protein</div>
                </div>
                <div className="text-center p-3 bg-blue-100 dark:bg-blue-950/30 rounded-xl">
                  <div className="font-bold text-2xl text-blue-600">{entry.carbs_g || 0}g</div>
                  <div className="text-xs text-muted-foreground">Carbs</div>
                </div>
                <div className="text-center p-3 bg-orange-100 dark:bg-orange-950/30 rounded-xl">
                  <div className="font-bold text-2xl text-orange-600">{entry.fat_g || 0}g</div>
                  <div className="text-xs text-muted-foreground">Fat</div>
                </div>
                <div className="text-center p-3 bg-pink-100 dark:bg-pink-950/30 rounded-xl">
                  <div className="font-bold text-2xl text-pink-600">{entry.sugar_g || 0}g</div>
                  <div className="text-xs text-muted-foreground">Sugar</div>
                </div>
              </div>
            </div>
          )}

          {/* Micronutrients */}
          {entry.is_finalized && (
            <div className="p-4 bg-green-500/5 rounded-xl">
              <p className="font-bold text-green-600 mb-3 flex items-center gap-2">
                <Microscope className="w-4 h-4" />
                Micronutrients
              </p>
              
              {hasMicronutrients ? (
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-2 bg-green-100 dark:bg-green-950/30 rounded-lg">
                    <div className="font-bold text-green-600">{entry.vitamin_a_mcg || 0}</div>
                    <div className="text-xs text-muted-foreground">Vit A (mcg)</div>
                  </div>
                  <div className="text-center p-2 bg-green-100 dark:bg-green-950/30 rounded-lg">
                    <div className="font-bold text-green-600">{entry.vitamin_c_mg || 0}</div>
                    <div className="text-xs text-muted-foreground">Vit C (mg)</div>
                  </div>
                  <div className="text-center p-2 bg-green-100 dark:bg-green-950/30 rounded-lg">
                    <div className="font-bold text-green-600">{entry.calcium_mg || 0}</div>
                    <div className="text-xs text-muted-foreground">Calcium (mg)</div>
                  </div>
                  <div className="text-center p-2 bg-green-100 dark:bg-green-950/30 rounded-lg">
                    <div className="font-bold text-green-600">{entry.iron_mg || 0}</div>
                    <div className="text-xs text-muted-foreground">Iron (mg)</div>
                  </div>
                  <div className="text-center p-2 bg-green-100 dark:bg-green-950/30 rounded-lg">
                    <div className="font-bold text-green-600">{entry.potassium_mg || 0}</div>
                    <div className="text-xs text-muted-foreground">Potassium (mg)</div>
                  </div>
                  <div className="text-center p-2 bg-green-100 dark:bg-green-950/30 rounded-lg">
                    <div className="font-bold text-green-600">{entry.folate_mcg || 0}</div>
                    <div className="text-xs text-muted-foreground">Folate (mcg)</div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    Micronutrient data not yet analyzed
                  </p>
                  <Button
                    data-testid="analyze-micronutrients-button"
                    onClick={() => onAnalyzeMicronutrients(entry.id)}
                    disabled={analyzingMicros}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                  >
                    {analyzingMicros ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Microscope className="w-4 h-4 mr-2" />
                        Analyze Micronutrients
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {/* Relabel selector */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">Change meal type:</span>
              <Select
                value={entry.meal_type}
                onValueChange={(value) => onRelabel(entry.id, value)}
                disabled={relabelling === entry.id}
              >
                <SelectTrigger className="w-40" data-testid="meal-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Breakfast</SelectItem>
                  <SelectItem value="lunch">Lunch</SelectItem>
                  <SelectItem value="dinner">Dinner</SelectItem>
                  <SelectItem value="snack">Snack</SelectItem>
                  <SelectItem value="drink">Drink</SelectItem>
                  <SelectItem value="late_night">Late Night</SelectItem>
                </SelectContent>
              </Select>
              {relabelling === entry.id && <Loader2 className="w-4 h-4 animate-spin" />}
            </div>

            {/* Undo finalization button */}
            {entry.is_finalized && !entry.finalization_undone_at && (
              <Button
                data-testid="undo-finalize-button"
                variant="outline"
                onClick={() => onUndoFinalize(entry.id)}
                disabled={undoingFinalization === entry.id}
                className="w-full"
              >
                {undoingFinalization === entry.id ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Undoing...
                  </>
                ) : (
                  <>
                    <Undo2 className="w-4 h-4 mr-2" />
                    Undo Finalization (Edit Entry)
                  </>
                )}
              </Button>
            )}

          </div>
        </div>
      </motion.div>

      {/* Micronutrient reminder popup */}
      {showMicronutrientReminder && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-4 right-4 max-w-sm p-4 bg-green-600 text-white rounded-xl shadow-xl z-60"
        >
          <p className="font-medium mb-2">Micronutrient Analysis Available!</p>
          <p className="text-sm opacity-90 mb-3">
            Click &quot;Analyze Micronutrients&quot; to get detailed vitamin and mineral data for this meal.
          </p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowMicronutrientReminder(false)}
          >
            Got it
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default FoodEntryDetailModal;
